/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';

/**
 * **A drag that ends on the backdrop is not a click on the backdrop** (reported by Todd 2026-09-03
 * against the clone dialog: *"scrolling out of the text field closes the dialog on clone"*).
 *
 * A `click` fires on the nearest common ancestor of `mousedown` and `mouseup`. So selecting the name
 * in a dialog's text field and dragging past the edge of the dialog — which is what selecting a
 * whole value feels like — releases on the backdrop, the browser reports a click **there**, and the
 * dialog dismisses with the operator's typing in it. The inner `stopPropagation` cannot help: the
 * click never passed through the dialog at all.
 *
 * That makes it an **exit nobody chose**, which is worse than the exits
 * *an exit is not a decision — prompt before discarding work* is written about. Those are at least
 * gestures; this one is the operator selecting text.
 *
 * ## Why a shared hook rather than a fix in the dialog that was reported
 *
 * *A rule attached to one path lapses when another is added.* Three overlays in this console dismiss
 * on a backdrop click and every one of them had this — the clone dialog, `ConfirmDialog`, and the
 * round plan's override editor, which holds a **whole draft of override rows** and is therefore the
 * expensive one. Fixing the reported dialog would have left the other two, and the next overlay
 * would have been written from one of them.
 *
 * `MobileNavDrawer` is deliberately **not** a caller: its backdrop is a sibling of the drawer rather
 * than its ancestor, so a release on it is already only ever a real click on it. Structure answered
 * the question there, and adding the hook would imply it had the defect.
 *
 * ## What it guards, in both directions — and the second one is not free
 *
 * Dismissal requires the press **and** the release to be on the backdrop itself.
 *
 * Press-inside → release-outside is the reported case. The mirror — press on the backdrop, release
 * on the dialog — needs its **own** handler rather than falling out of the click's target, and that
 * is worth stating because the first version of this hook claimed to guard it and did not: the
 * common ancestor of the backdrop and the dialog **is the backdrop**, so the browser reports that
 * drag as a click whose target is the backdrop, indistinguishable from a real one. Caught by
 * driving it through a DOM; every check on the click alone passes it.
 *
 * @param onDismiss what a real backdrop click means — usually the surface's own Cancel.
 * @param options.disabled dismissal is off entirely (a write in flight has no readable outcome yet).
 */
export function useBackdropDismiss(
  onDismiss: () => void,
  options?: { disabled?: boolean },
): {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
} {
  // Refs rather than state: nothing renders from them, and a re-render between press and release
  // would drop the fact they exist to carry.
  const pressedOnBackdrop = React.useRef(false);
  const releasedOnBackdrop = React.useRef(false);

  return {
    onPointerDown: (event) => {
      pressedOnBackdrop.current = event.target === event.currentTarget;
    },
    onPointerUp: (event) => {
      releasedOnBackdrop.current = event.target === event.currentTarget;
    },
    onClick: (event) => {
      const startedHere = pressedOnBackdrop.current;
      const endedHere = releasedOnBackdrop.current;
      // Cleared whatever the outcome, so the next click is judged on its own press and release
      // rather than on a stale one from a gesture this hook refused.
      pressedOnBackdrop.current = false;
      releasedOnBackdrop.current = false;
      if (options?.disabled) return;
      if (!startedHere || !endedHere || event.target !== event.currentTarget) return;
      onDismiss();
    },
  };
}
